import { ManufacturerMatchingServiceType } from "./manufacturer-matching.service.type";
import {
  getManufacturers,
  updateManufacturer,
  getParts,
  doc2Model,
  getMatches,
  createMatchDoc,
  updateMatchedManufacturersList,
  getMatchedManufacturersList,
  createMatchedManufacturersList
} from "../api.service";
import {
  Manufacturer,
  MatchedManufacturer
} from "../../../../shared/models/manufacturer";
import { Part } from "../../../../shared/models/part";
import { ManufacturerService } from "../manufacturer/manufacturer.service";
import { Inject, Injectable } from "@angular/core";
import { AppType, APP_TYPE } from "../app.type";
import { userEditedFields } from "../utils/app.utils";
import {
  ObsolescenceStatus,
  EuRoHSStatus
} from "../../../../shared/models/rm-common-properties";
import { DataProvider } from "../../../../shared/constants/dataProvider";
import { Z2DataMatch } from "../../../../shared/models/dataProviders/z2data";
import { convertYearsLeftToDate } from "../utils/date.util";
import { StringUtils } from "../../../../shared/utils/string.utils";

@Injectable()
export class ManufacturerMatchingService implements ManufacturerMatchingServiceType {
  manufacturer = new Manufacturer();

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  async updateManufacturer(manufacturer: Manufacturer, oldDoc?: Manufacturer) {
    const fields = userEditedFields(
      doc2Model("manufacturer", manufacturer),
      doc2Model("manufacturer", oldDoc)
    );
    const manufacturerEditedFields = fields.filter(
      (val: any) => val != undefined && val != "manufacturerEditedFields"
    );
    manufacturer["manufacturerEditedFields"] = manufacturerEditedFields;

    const saveResult: any = await updateManufacturer(
      this.app.customers.expectCurrent,
      "manufacturer",
      manufacturer,
      oldDoc
    );

    if (!saveResult?.ok || !saveResult.id || !saveResult.rev) {
      throw new Error("The manufacturer was not confirmed as saved.");
    }

    // Keep latest DB revision/id on the in-memory model to avoid stale-save conflicts.
    if (saveResult && saveResult.rev) {
      manufacturer._rev = saveResult.rev;
    }
    if (saveResult && saveResult.id) {
      manufacturer._id = saveResult.id;
    }

    this.app.state.next({
      hasSuccess: true,
      successText: this.app.text.matchingManufacturerModal.savedSuccesfully
    });
    setTimeout(() => {
      this.app.state.next({ hasSuccess: false });
    }, 1000);

    return manufacturer;
  }

  async getCurrentManufacturer(manufacturerId?: string): Promise<Manufacturer> {
    if (manufacturerId === null || manufacturerId === undefined) {
      return {} as Manufacturer;
    }

    const manufacturer = await getManufacturers(
      this.app.customers.expectCurrent,
      [manufacturerId]
    );

    let manufacturerDoc = manufacturer[0];
    let dataProviderId = manufacturerDoc.z2DataId;
    const partNumber = !StringUtils.isNullOrEmpty(
      manufacturerDoc.manufacturerPartNumberClean
    )
      ? manufacturerDoc.manufacturerPartNumberClean
      : manufacturerDoc.manufacturerPartNumberRaw;
    
    manufacturerDoc.matches = await this.getManufacturerMatches(
      partNumber,
      dataProviderId,
      manufacturerDoc.obsolescenceStatusOverride
    );

    if (manufacturerDoc.matches) {
      manufacturerDoc = this.parseMatchesData(manufacturerDoc);
    }
    this.manufacturer = manufacturerDoc;
    return manufacturerDoc;
  }

  async getManufacturerMatches(
    manufacturerPartNumber: string | undefined,
    dataProviderId: any,
    isManualOverride: boolean
  ): Promise<Z2DataMatch[]> {
    if (
      manufacturerPartNumber == null ||
      isManualOverride === true
    ) {
      return [];
    }

    const matches = await getMatches(
      this.app.customers.expectCurrent,
      manufacturerPartNumber,
      this.app.customers.dataProvider,
      dataProviderId
    );

    return matches;
  }

  async getCurrentPart(partNumber: string) {
    if (partNumber == null) {
      return {} as Part;
    }

    const part = await getParts(this.app.customers.expectCurrent, [partNumber]);

    const partDoc = part[0];
    return partDoc;
  }

  async deleteMatch(manufacturer: Manufacturer, match: Z2DataMatch) {
    const doc = new Manufacturer();
    Object.keys(manufacturer).forEach((key) => {
      doc[key] = manufacturer[key];
    });
    manufacturer.z2DataId = "";
    await this.updateManufacturer(doc, manufacturer);
    if (manufacturer._id == null) {
      return;
    }
    this.app.manufacturerMatching.getCurrentManufacturer(manufacturer._id);
  }

  isSpecialColumn(column: string) {
    if (
      column === "PartNumber" ||
      column === "Manufacturer" ||
      column === "Description" ||
      column === "YEOL" ||
      column === "MPN" ||
      column === "ReachStatus" ||
      column === "REACHStatus" ||
      column === "MatchReason"
    ) {
      return false;
    }
    return true;
  }

  getTooltipMessage(manufacturer: Manufacturer) {
    const currentMatch =
      this.app.manufacturer.getMatchingStatusOfManufacturer(manufacturer);
    return (this.app.text.RM as any)[currentMatch];
  }

  async createMatch(manufacturer: Manufacturer, match: Z2DataMatch) {
    const manufacturerDoc = new Manufacturer();
    if (manufacturer == null) {
      return;
    }
    Object.keys(manufacturer).forEach((key) => {
      manufacturerDoc[key] = manufacturer[key];
    });

    manufacturerDoc["manufacturerPartDescription"] = match["Description"];
    manufacturerDoc["name"] = match["Manufacturer"];
    manufacturerDoc["edited"] = true;
    if (manufacturerDoc["searchResults"] === true) {
      manufacturerDoc["matches"] = [];
    }
    manufacturerDoc["searchResults"] = false;
    manufacturerDoc["obsolescenceStatusOverride"] = false;
    manufacturerDoc["datasheet"] = match["Datasheet"];
    manufacturerDoc["euRoHSVersion"] = match["RoHSVersion"];
    manufacturerDoc["reachStatus"] =
      match["ReachStatus"] ?? match["REACHStatus"] ?? match["ReachSatus"];
    manufacturerDoc["manufacturerPartNumber"] = match["MPN"];
    manufacturerDoc["obsolescenceStatus"] = match[
      "LifecycleStatus"
    ] as ObsolescenceStatus;
    manufacturerDoc["euRoHS"] = match["RoHSStatus"] as EuRoHSStatus;
    manufacturerDoc["taxonomy"] = match["Category"];
    manufacturerDoc["estimatedYearsToEOL"] =
      match["EstimatedYearsToEOL"].toString();
    manufacturerDoc["estimatedEOLDate"] = convertYearsLeftToDate(
      manufacturerDoc["estimatedYearsToEOL"]
    );
    manufacturerDoc["z2DataId"] = match["PartID"].toString();

    if (manufacturerDoc == null) {
      return;
    }
    await this.app.manufacturerMatching.updateManufacturer(
      manufacturerDoc,
      manufacturer
    );
    if (manufacturerDoc._id == null) {
      return;
    }
    await this.app.manufacturerMatching.getCurrentManufacturer(
      manufacturerDoc._id
    );

    // create match document
    const mpn = match.MPN;
    await createMatchDoc(
      this.app.customers.expectCurrent,
      this.app.customers.dataProvider,
      mpn,
      match.Manufacturer
    );

    let existingMatches = await getMatchedManufacturersList(
      this.app.customers.expectCurrent
    );
    if (existingMatches == null) {
      await this.app.manufacturerMatching.createMatchedManufacturersList(
        this.app.customers.expectCurrent
      );
      existingMatches = await getMatchedManufacturersList(
        this.app.customers.expectCurrent
      );
    }
    let alreadyInList = existingMatches.some(
      (m: MatchedManufacturer) =>
        m.manufacturerPartNumber === mpn && m.name === match.Manufacturer
    );

    if (!alreadyInList) {
      await this.app.operations.updateWorkCount();
      await this.app.manufacturerMatching.updateMatchedManufacturersList(
        this.app.customers.expectCurrent,
        mpn,
        match.Manufacturer
      );
    }
  }

  async cleanUpData(match: Z2DataMatch, fields: Set<any>) {
    const defaultManufacturer = new Manufacturer();
    const manufacturerDoc = new Manufacturer();
    const oldManufacturerDoc = new Manufacturer();
    const availableMatchesCount = Array.isArray(match.matches)
      ? match.matches.length
      : Math.max(Number(match.numberOfMatches) || 0, Number(match.matchesCount) || 0);

    Object.keys(match).forEach((key) => {
      manufacturerDoc[key] = match[key];
      oldManufacturerDoc[key] = match[key];
    });
    fields.forEach((key) => {
      key = key.split(".")[1];
      manufacturerDoc[key] = defaultManufacturer[key];
    });
    if (manufacturerDoc == null) {
      return;
    }
    if (
      manufacturerDoc.manufacturerPartNumberRaw != null &&
      manufacturerDoc.nameRaw != ""
    ) {
      manufacturerDoc.manufacturerPartNumber =
        manufacturerDoc.manufacturerPartNumberRaw;
    }

    if (manufacturerDoc.nameRaw != null && manufacturerDoc.nameRaw != "") {
      manufacturerDoc.name = manufacturerDoc.nameRaw;
    }
    manufacturerDoc.edited = false;
    manufacturerDoc.matchReview = false;
    manufacturerDoc.z2DataId = "";
    manufacturerDoc.numberOfMatches =
      availableMatchesCount > 0 ? String(availableMatchesCount) : "";
    manufacturerDoc.matchesCount =
      availableMatchesCount > 0 ? String(availableMatchesCount) : "";
    manufacturerDoc.manufacturerPartDescription = "";
    manufacturerDoc.datasheet = "";
    await this.app.manufacturerMatching.updateManufacturer(
      manufacturerDoc,
      oldManufacturerDoc
    );
    if (manufacturerDoc._id == null) {
      return;
    }
    await this.app.manufacturerMatching.getCurrentManufacturer(
      manufacturerDoc._id
    );
  }

  getColumn(column: string): string {
    return this.getZ2DataColumn(column);
  }

  getZ2DataColumn(column: string): string {
    switch (column) {
      case "manufacturer.manufacturerPartNumber":
        return "manufacturer.PartID";
      case "manufacturer.name":
        return "manufacturer.Manufacturer";
      case "manufacturer.manufacturerPartDescription":
        return "manufacturer.Description";
      case "manufacturer.datasheet":
        return "manufacturer.Datasheet";
      default:
        return "manufacturer.MPN";
    }
  }

  updateCurrentMatchIfChangesExist(
    currentManufacturer: Manufacturer,
    matches: Z2DataMatch[]
  ) {
    const dataProviderId = currentManufacturer.z2DataId;
    matches.forEach((match) => {
      // If current manufacturer status is different than the current match status -> update the match without saving it to the database
      if (
        match.ComID === dataProviderId &&
        currentManufacturer.obsolescenceStatus !== match.Lifecycle
      ) {
        match.Lifecycle =
          currentManufacturer.obsolescenceStatus != null
            ? currentManufacturer.obsolescenceStatus
            : "";
        match.RoHS =
          currentManufacturer.euRoHS != null ? currentManufacturer.euRoHS : "";
        match.Datasheet =
          currentManufacturer.datasheet != null
            ? currentManufacturer.datasheet
            : "";
      }
    });
  }

  private parseMatchesData(manufacturerDoc: Manufacturer) {
    if (manufacturerDoc.matches != null) {
      manufacturerDoc.matches.forEach((match: Z2DataMatch) => {
        switch (this.app.customers.dataProvider) {
          case DataProvider.Z2DATA:
            match.LifecycleStatus = ManufacturerService.parseStatus(
              match.LifecycleStatus
            );
            match.RoHSStatus = ManufacturerService.parseRoHS(match.RoHSStatus);
            break;
          default:
            match.Lifecycle = ManufacturerService.parseStatus(match.Lifecycle);
            match.RoHS = ManufacturerService.parseRoHS(match.RoHS);
            break;
        }
      });
    }
    return manufacturerDoc;
  }

  async createMatchedManufacturersList(customer: string) {
    await createMatchedManufacturersList(customer);
  }

  async updateMatchedManufacturersList(
    customer: string,
    mpn: string,
    name: string
  ) {
    let list = [];
    let matchedManufacturer = new MatchedManufacturer();

    matchedManufacturer.manufacturerPartNumber = mpn;
    matchedManufacturer.name = name;
    list.push(matchedManufacturer);

    await updateMatchedManufacturersList(customer, list);
  }
}
